import {
  CostingMethod,
  CreateItemParams,
  CustomFieldValues,
  DimensionUnit,
  getDefaultCostingMethod,
  HydratedItem,
  ItemCost,
  ItemMargin,
  ItemType,
  ItemTypeEnum,
  ItemUnit,
  ItemUnitConversion,
  ItemUnitEnum,
  WeightUnit,
} from "@forgepoint/shared";
import { createId } from "@paralleldrive/cuid2";

/** The editable fields of the item editor, as one form object. */
export interface ItemFormData {
  name: string;
  revision: string;
  type: ItemType;
  costingMethod: CostingMethod;
  unit: ItemUnit;
  weight: string | null;
  weightUnit: WeightUnit | null;
  dimensionUnit: DimensionUnit | null;
  length: string | null;
  width: string | null;
  height: string | null;
  allowance: string | null;
  unitConversions: ItemUnitConversion[];
  costBreaks: ItemCost[];
  description?: string;
  briefing: string;
  organizationFileIds?: string[];
  primaryFileAssetId?: string | null;
  fileAssetIds?: string[];
  margin?: ItemMargin[];
  priceModelId?: string | null;
  priceModelInputs?: Record<string, unknown>;
  customFields: CustomFieldValues;
}

/** Seeds a create-mode form from optional initial values. */
export function buildCreateFormData(
  seed?: Partial<CreateItemParams>,
): ItemFormData {
  const type = seed?.type ?? ItemTypeEnum.MATERIAL;
  return {
    name: seed?.name ?? "",
    revision: seed?.revision ?? "",
    type,
    costingMethod: seed?.costingMethod ?? getDefaultCostingMethod(type),
    unit: seed?.unit ?? ItemUnitEnum.each,
    weight: null,
    weightUnit: null,
    dimensionUnit: null,
    length: null,
    width: null,
    height: null,
    allowance: null,
    unitConversions: [],
    costBreaks: (seed?.costBreaks ?? []).map((costBreak) => ({
      ...costBreak,
      id: createId(),
      itemId: "",
    })),
    description: seed?.description ?? "",
    briefing: seed?.briefing ?? "",
    organizationFileIds: seed?.organizationFileIds ?? [],
    primaryFileAssetId: null,
    fileAssetIds: [],
    margin: [],
    priceModelId: null,
    priceModelInputs: undefined,
    customFields: seed?.customFields ?? {},
  };
}

/** Projects a server item onto the editable form fields. */
export function getFormDataFromItem(item: HydratedItem): ItemFormData {
  return {
    name: item.name,
    revision: item.revision || "",
    type: item.type,
    costingMethod: item.costingMethod,
    unit: item.unit,
    weight: item.weight ?? null,
    weightUnit: item.weightUnit ?? null,
    dimensionUnit: item.dimensionUnit ?? null,
    length: item.length ?? null,
    width: item.width ?? null,
    height: item.height ?? null,
    allowance: item.allowance ?? null,
    unitConversions: item.unitConversions ?? [],
    costBreaks: item.costBreaks || [],
    description: item.description || "",
    briefing: item.briefing || "",
    organizationFileIds: item.organizationFileIds || [],
    primaryFileAssetId: item.primaryFileAssetId || null,
    fileAssetIds: item.fileAssetIds || [],
    margin: item.margin || [],
    priceModelId: item.priceModelId || null,
    priceModelInputs: item.priceModelInputs || undefined,
    customFields: item.customFields ?? {},
  };
}
